import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { reportSchedulesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { CalendarClock, Send, Pencil, Pause, Play, Trash2, Loader2, FileSearch } from 'lucide-react';
import PdfPreviewModal from './PdfPreviewModal';

// Human label per schedulable report_key (the list is no longer sales-only).
const REPORT_TYPE_LABEL = {
    sales_report: { key: 'reportSchedules.salesReportType', fallback: 'Sales Report' },
    employee_taps: { key: 'reportSchedules.employeeTapsReport', fallback: 'Employee Tap Report' },
    review_calculations_onepager: { key: 'salesCalc.onePagerTitle', fallback: 'Review Calculations' },
};

const STATUS_STYLES = {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    paused: 'bg-amber-100 text-amber-700 border-amber-200',
    completed: 'bg-slate-100 text-slate-600 border-slate-200',
};

const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

const ScheduledReportsList = ({ organizations, refreshNonce, onEdit }) => {
    const { t } = useTranslation();
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningId, setRunningId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [previewingId, setPreviewingId] = useState(null);
    const [preview, setPreview] = useState({ open: false, loading: false, error: false, pages: [], scheduleId: null });

    const orgName = useCallback((id) => organizations.find(o => o.id === id)?.name || id, [organizations]);

    const load = useCallback(async () => {
        try {
            // No report_key filter: Sales Report and Employee Tap Report
            // schedules live side by side in this one list.
            const res = await reportSchedulesAPI.list();
            setSchedules(res.data.schedules || []);
        } catch {
            // roles without schedule access simply see nothing
            setSchedules([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshNonce]);

    const runNow = async (s) => {
        setRunningId(s.id);
        try {
            const res = await reportSchedulesAPI.runNow(s.id);
            const run = res.data;
            if (run.status === 'success') {
                toast.success(t('salesReport.schedule.runOk', { n: run.emails_sent }));
            } else {
                toast.error(t('salesReport.schedule.runFail', { error: run.error || '' }));
            }
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('salesReport.schedule.runFail', { error: '' }));
        } finally {
            setRunningId(null);
        }
    };

    // Read-only preview of the saved schedule config — never sends or
    // modifies anything; reuses the shared server-rasterized PDF viewer.
    const previewSchedulePdf = async (scheduleId) => {
        setPreviewingId(scheduleId);
        setPreview({ open: true, loading: true, error: false, pages: [], scheduleId });
        try {
            const res = await reportSchedulesAPI.schedulePreviewImages(scheduleId);
            const pages = res.data?.pages || [];
            setPreview({ open: true, loading: false, error: !pages.length, pages, scheduleId });
        } catch (error) {
            setPreview({ open: true, loading: false, error: true, pages: [], scheduleId });
        } finally {
            setPreviewingId(null);
        }
    };

    const toggle = async (s) => {
        try {
            await reportSchedulesAPI.toggle(s.id);
            toast.success(s.active ? t('salesReport.schedule.pausedToast') : t('salesReport.schedule.resumedToast'));
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed');
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await reportSchedulesAPI.remove(deleteTarget.id);
            toast.success(t('salesReport.schedule.deleted'));
            setDeleteTarget(null);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed');
        }
    };

    const freqLabel = (s) => {
        const base = t(`salesReport.schedule.freq.${s.frequency}`);
        if (s.frequency === 'weekly' && s.days_of_week?.length) {
            return `${base} · ${s.days_of_week.map(d => t(`salesReport.schedule.days.${d}`)).join(', ')}`;
        }
        if (s.frequency === 'monthly' && s.day_of_month) {
            return `${base} · ${t('salesReport.schedule.dayShort')} ${s.day_of_month}`;
        }
        if (s.frequency === 'one_time' && s.run_date) {
            return `${base} · ${s.run_date}`;
        }
        return base;
    };

    const recipEmails = (s) => (s.recipients || []).map(r => (typeof r === 'string' ? r : r.email));

    const langSummary = (s) => {
        const langs = [...new Set((s.recipients || []).map(r =>
            typeof r === 'string' ? 'auto' : (r.language || 'auto')))];
        return langs.map(l => l === 'auto' ? t('salesReport.schedule.langAuto') : l.toUpperCase()).join(' / ');
    };

    if (!loading && !schedules.length) return null;

    return (
        <Card data-testid="scheduled-reports-card" data-insight-id="scheduled-reports-card">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-purple-600" />
                    {t('salesReport.schedule.listTitle')}
                </CardTitle>
                <CardDescription>{t('salesReport.schedule.listDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('salesReport.schedule.cols.name')}</TableHead>
                                    <TableHead>{t('salesReport.schedule.cols.frequency')}</TableHead>
                                    <TableHead>{t('salesReport.schedule.cols.restaurants')}</TableHead>
                                    <TableHead>{t('salesReport.schedule.cols.recipients')}</TableHead>
                                    <TableHead>{t('salesReport.schedule.cols.nextRun')}</TableHead>
                                    <TableHead>{t('salesReport.schedule.cols.lastRun')}</TableHead>
                                    <TableHead>{t('salesReport.schedule.cols.status')}</TableHead>
                                    <TableHead className="text-right">{t('salesReport.schedule.cols.actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {schedules.map(s => (
                                    <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                                        <TableCell className="font-medium">
                                            {s.name}
                                            <div className="text-xs text-muted-foreground">{langSummary(s)}</div>
                                        </TableCell>
                                        <TableCell className="text-sm">{freqLabel(s)}<div className="text-xs text-muted-foreground">{s.delivery_time} · {s.timezone}</div></TableCell>
                                        <TableCell className="text-sm max-w-[180px]">
                                            {(s.organization_ids || []).length
                                                ? s.organization_ids.map(orgName).join(', ')
                                                : t('salesReport.schedule.allRestaurants')}
                                        </TableCell>
                                        <TableCell className="text-sm" title={recipEmails(s).join(', ')}>
                                            {recipEmails(s).length <= 1
                                                ? recipEmails(s)[0] || '—'
                                                : t('salesReport.schedule.recipientCount', { n: recipEmails(s).length })}
                                        </TableCell>
                                        <TableCell className="text-sm">{s.active ? fmtDateTime(s.next_run_at) : '—'}</TableCell>
                                        <TableCell className="text-sm">
                                            {fmtDateTime(s.last_run_at)}
                                            {s.last_run_status && (
                                                <span className={`ml-1 ${s.last_run_status === 'success' ? 'text-emerald-600' : 'text-red-500'}`}>
                                                    {s.last_run_status === 'success' ? '●' : '●'}
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap items-center gap-1">
                                                <Badge variant="outline" className={STATUS_STYLES[s.status] || ''} data-testid={`schedule-status-${s.id}`}>
                                                    {t(`salesReport.schedule.status.${s.status}`)}
                                                </Badge>
                                                <Badge variant="secondary" className="text-[10px]" data-testid={`schedule-report-type-${s.id}`}>
                                                    {REPORT_TYPE_LABEL[s.report_key]
                                                        ? t(REPORT_TYPE_LABEL[s.report_key].key, REPORT_TYPE_LABEL[s.report_key].fallback)
                                                        : (s.report_key || '—')}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button size="icon" variant="ghost" title={t('salesReport.previewPdf')}
                                                    aria-label={t('salesReport.previewPdf')}
                                                    onClick={() => previewSchedulePdf(s.id)} disabled={previewingId === s.id}
                                                    data-testid={`schedule-preview-pdf-${s.id}`}>
                                                    {previewingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSearch className="w-4 h-4" />}
                                                </Button>
                                                <Button size="icon" variant="ghost" title={t('salesReport.schedule.actions.runNow')}
                                                    onClick={() => runNow(s)} disabled={runningId === s.id}
                                                    data-testid={`schedule-run-now-${s.id}`}>
                                                    {runningId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                </Button>
                                                <Button size="icon" variant="ghost" title={t('salesReport.schedule.actions.edit')}
                                                    onClick={() => onEdit(s)} data-testid={`schedule-edit-${s.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost"
                                                    title={s.active ? t('salesReport.schedule.actions.pause') : t('salesReport.schedule.actions.resume')}
                                                    onClick={() => toggle(s)} data-testid={`schedule-toggle-${s.id}`}>
                                                    {s.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-600"
                                                    title={t('salesReport.schedule.actions.delete')}
                                                    onClick={() => setDeleteTarget(s)} data-testid={`schedule-delete-${s.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="schedule-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('salesReport.schedule.deleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('salesReport.schedule.deleteDesc', { name: deleteTarget?.name })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="schedule-delete-cancel">{t('salesReport.schedule.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="schedule-delete-confirm">
                            {t('salesReport.schedule.actions.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <PdfPreviewModal
                open={preview.open}
                onOpenChange={(o) => setPreview(p => ({ ...p, open: o }))}
                loading={preview.loading}
                error={preview.error}
                pages={preview.pages}
                onRetry={() => preview.scheduleId && previewSchedulePdf(preview.scheduleId)}
                title={t('salesReport.pdfPreview.title')}
            />
        </Card>
    );
};

export default ScheduledReportsList;
